/**
 * React Hook for GitHub Data Enrichment
 *
 * Provides perspective-oriented portfolio intelligence based on viewer profile.
 * Automatically adapts content based on Layer 3 (ViewerProfile) context.
 */

import { useQuery } from '@tanstack/react-query'
import { useViewerProfile } from '@/contexts/ViewerProfileContext'
import {
  getGitHubEnrichmentService,
  type GitHubEnrichmentData,
  type RecruiterView,
  type TechnicalView,
  type ExecutiveView,
  type PublicView
} from '@/services/github-enrichment.service'
import { getMockEnrichmentData } from '@/services/github-enrichment-mock.service'

// ============================================================================
// Hook: useGitHubEnrichment
// ============================================================================

export function useGitHubEnrichment() {
  return useQuery({
    queryKey: ['github-enrichment'],
    queryFn: async () => {
      // Check if GitHub token is available
      const hasToken = !!import.meta.env.VITE_GITHUB_TOKEN

      if (!hasToken) {
        console.log('[GitHub Enrichment] Using mock data (no GitHub token configured)')
        // Use mock data when GitHub API isn't available
        return getMockEnrichmentData()
      }

      try {
        const service = getGitHubEnrichmentService()
        return await service.enrichUserProfile()
      } catch (error) {
        console.warn('[GitHub Enrichment] API failed, falling back to mock data:', error)
        return getMockEnrichmentData()
      }
    },
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours (previously cacheTime)
    retry: 1 // Reduced from 2 since we have mock fallback
  })
}

// ============================================================================
// Hook: usePerspectiveView
// ============================================================================

/**
 * Get perspective-specific view based on current viewer profile
 */
export function usePerspectiveView() {
  const { currentProfile } = useViewerProfile()
  const { data, isLoading, error } = useGitHubEnrichment()

  if (isLoading || !data) {
    return {
      isLoading,
      error,
      view: null as RecruiterView | TechnicalView | ExecutiveView | PublicView | null,
      viewType: currentProfile
    }
  }

  const view = getPerspectiveView(data, currentProfile)

  return {
    isLoading: false,
    error: null,
    view,
    viewType: currentProfile,
    fullData: data
  }
}

/**
 * Get the appropriate view based on viewer profile
 */
function getPerspectiveView(
  data: GitHubEnrichmentData,
  profile: string
): RecruiterView | TechnicalView | ExecutiveView | PublicView {
  switch (profile) {
    case 'recruiter-view':
      return data.perspectives.recruiter
    case 'technical-view':
      return data.perspectives.technical
    case 'executive-view':
      return data.perspectives.executive
    default:
      return data.perspectives.public
  }
}

// ============================================================================
// Hook: usePortfolioHighlights
// ============================================================================

/**
 * Get top portfolio highlights for landing page
 */
export function usePortfolioHighlights() {
  const { data } = useGitHubEnrichment()

  if (!data) {
    return {
      topProjects: [] as Array<{
        name: string
        description: string
        stars: number
        forks: number
        techStack: string[]
        url: string
        innovationScore: number
      }>,
      keyMetrics: [] as Array<{
        label: string
        value: string | number
        context: string
      }>,
      technicalSkills: [] as Array<{
        category: string
        skills: string[]
      }>,
      achievements: [] as string[]
    }
  }

  const topProjects = data.repositories
    .sort((a: any, b: any) => (b.stars + b.forks * 2) - (a.stars + a.forks * 2))
    .slice(0, 5)
    .map((repo: any) => ({
      name: repo.name,
      description: repo.description,
      stars: repo.stars,
      forks: repo.forks,
      techStack: repo.techStack.slice(0, 4),
      url: repo.url,
      innovationScore: repo.innovationScore
    }))

  const keyMetrics = [
    {
      label: 'Total Projects',
      value: data.repositories.length,
      context: 'Active repositories'
    },
    {
      label: 'Community Stars',
      value: data.businessImpact.totalStars,
      context: 'Open source recognition'
    },
    {
      label: 'Cost Savings',
      value: `$${data.businessImpact.costSavingsAchieved}`,
      context: 'Annual infrastructure savings'
    },
    {
      label: 'Code Complexity',
      value: data.technicalProfile.averageCodeComplexity,
      context: 'Average project complexity'
    }
  ]

  const technicalSkills = [
    {
      category: 'Languages',
      skills: data.technicalProfile.primaryLanguages
    },
    {
      category: 'Frameworks',
      skills: data.technicalProfile.frameworks
    },
    {
      category: 'Databases',
      skills: data.technicalProfile.databaseSystems
    },
    {
      category: 'Cloud Platforms',
      skills: data.technicalProfile.cloudPlatforms
    }
  ]

  const achievements = [
    `${data.leadershipProfile.projectsOwned} projects owned and delivered`,
    `${data.businessImpact.totalStars}+ stars from open source community`,
    `${Math.round(data.businessImpact.costSavingsAchieved / 12)}/month average cost savings per project`,
    `${data.technicalProfile.architecturePatterns.length} different architecture patterns mastered`
  ]

  return {
    topProjects,
    keyMetrics,
    technicalSkills,
    achievements
  }
}

// ============================================================================
// Hook: useRecruiterInsights
// ============================================================================

/**
 * Get recruiter-specific insights
 */
export function useRecruiterInsights() {
  const { data } = useGitHubEnrichment()

  if (!data) {
    return null
  }

  return data.perspectives.recruiter
}

// ============================================================================
// Hook: useTechnicalInsights
// ============================================================================

/**
 * Get technical-specific insights
 */
export function useTechnicalInsights() {
  const { data } = useGitHubEnrichment()

  if (!data) {
    return null
  }

  return data.perspectives.technical
}

// ============================================================================
// Hook: useExecutiveInsights
// ============================================================================

/**
 * Get executive-specific insights
 */
export function useExecutiveInsights() {
  const { data } = useGitHubEnrichment()

  if (!data) {
    return null
  }

  return data.perspectives.executive
}
